import type { SupportAnalyticsData, SupportMessage } from './types'

export const supportUtils = {
  // Filtering
  filterByStatus: (messages: SupportMessage[], status: string) =>
    messages.filter((message) => message.status === status),

  filterByPriority: (messages: SupportMessage[], priority: string) =>
    messages.filter((message) => message.priority === priority),

  filterByUser: (messages: SupportMessage[], userId: string) => messages.filter((message) => message.userId === userId),

  // Sorting
  sortByCreatedDate: (messages: SupportMessage[], order: 'asc' | 'desc' = 'desc') =>
    messages.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt.getTime() : a.createdAt.toDate().getTime()
      const dateB = b.createdAt instanceof Date ? b.createdAt.getTime() : b.createdAt.toDate().getTime()
      return order === 'desc' ? dateB - dateA : dateA - dateB
    }),

  sortByStatus: (messages: SupportMessage[]) =>
    messages.sort((a, b) => {
      const statusOrder = { open: 0, 'in-progress': 1, resolved: 2, closed: 3 }
      return statusOrder[a.status as keyof typeof statusOrder] - statusOrder[b.status as keyof typeof statusOrder]
    }),

  // Transformations
  toAnalyticsData: (messages: SupportMessage[]): SupportAnalyticsData[] =>
    messages
      .filter((message) => message.id && message.status && message.userName)
      .map((message) => ({
        id: message.id!,
        subject: message.subject,
        status: message.status!,
        priority: message.priority || 'medium',
        userName: message.userName!,
        companyName: message.companyName || 'N/A',
        createdAt: message.createdAt,
        updatedAt: message.updatedAt,
      })),

  // Status helpers
  getOpenMessages: (messages: SupportMessage[]) => supportUtils.filterByStatus(messages, 'open'),

  getInProgressMessages: (messages: SupportMessage[]) => supportUtils.filterByStatus(messages, 'in-progress'),

  getResolvedMessages: (messages: SupportMessage[]) => supportUtils.filterByStatus(messages, 'resolved'),

  // Statistics
  getStatusCounts: (messages: SupportMessage[]) => {
    const counts = { open: 0, 'in-progress': 0, resolved: 0, closed: 0 }
    for (const message of messages) {
      if (message.status && message.status in counts) {
        counts[message.status as keyof typeof counts]++
      }
    }
    return counts
  },

  getPriorityCounts: (messages: SupportMessage[]) => {
    const counts = { low: 0, medium: 0, high: 0 }
    for (const message of messages) {
      const priority = message.priority || 'medium'
      if (priority in counts) {
        counts[priority as keyof typeof counts]++
      }
    }
    return counts
  },

  // Search and filter
  searchMessages: (messages: SupportMessage[], searchTerm: string) => {
    const term = searchTerm.toLowerCase()
    return messages.filter(
      (message) =>
        message.subject.toLowerCase().includes(term) ||
        message.message.toLowerCase().includes(term) ||
        message.userName?.toLowerCase().includes(term) ||
        message.userEmail?.toLowerCase().includes(term) ||
        message.companyName?.toLowerCase().includes(term),
    )
  },
}
